// VIBRANIAM-SPACE v2 - ParticleFieldSubstrate.js
// Wraps the WASM ParticleField in a Three.js scene object and keeps the
// GPU buffers in sync with the simulation.

import * as THREE from 'three';
import { PARTICLE_VERT, PARTICLE_FRAG } from '../render/shaders.js';
import { packForceParams } from '../forces.js';

export class ParticleFieldSubstrate {
  /**
   * @param {object} module  the loaded particle-field WASM module
   * @param {number} capacity
   */
  constructor(module, capacity) {
    this.module = module;
    this.capacity = Math.max(1000, Math.min(400000, Math.round(capacity)));
    this.field = new module.ParticleField(this.capacity);

    this.scene = new THREE.Scene();
    this.geometry = new THREE.BufferGeometry();
    this._allocate(this.capacity);

    this.material = new THREE.ShaderMaterial({
      vertexShader: PARTICLE_VERT,
      fragmentShader: PARTICLE_FRAG,
      uniforms: {
        uViewportH: { value: 1080 },
        uSizeScale: { value: 0.04 },
        uGain: { value: 0.55 },
        uCore: { value: 6.0 },
      },
      transparent: true,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      vertexColors: true,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    this.points.frustumCulled = false;
    this.scene.add(this.points);

    this._chainSignature = '';
    this._weights = new Float32Array(0);
  }

  _allocate(capacity) {
    // Replacing attributes in place does not free their old GPU buffers - Three
    // only releases those when the geometry's 'dispose' event fires. Without
    // this, every setCapacity() call (e.g. dragging the Capacita slider) leaks
    // four buffers, which exhausts VRAM and takes the WebGL context down.
    this.geometry.dispose();
    const mk = (n, item) => {
      const a = new THREE.BufferAttribute(new Float32Array(n * item), item);
      a.setUsage(THREE.DynamicDrawUsage);
      return a;
    };
    this.geometry.setAttribute('position', mk(capacity, 3));
    this.geometry.setAttribute('color', mk(capacity, 3));
    this.geometry.setAttribute('aSize', mk(capacity, 1));
    this.geometry.setAttribute('aAlpha', mk(capacity, 1));
    this.geometry.setDrawRange(0, capacity);
    // The simulation is unbounded in principle; skip Three's culling maths.
    this.geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1e6);
  }

  setCapacity(capacity) {
    const want = Math.max(1000, Math.min(400000, Math.round(capacity)));
    if (want === this.capacity) return;
    this.capacity = want;
    this.field.setCapacity(want);
    this._allocate(want);
  }

  setViewportHeight(h) {
    this.material.uniforms.uViewportH.value = Math.max(1, h);
  }

  /**
   * @param {Array} forceNodes  resolved force descriptors
   *        [{ kind, weight, enabled, params }]
   */
  applyForces(forceNodes) {
    // Rebuilding the chain reallocates on the C++ side, so only do it when the
    // shape actually changed; weights and p-values are pushed every frame.
    const signature = forceNodes.map((f) => f.kind).join(',');
    if (signature !== this._chainSignature) {
      this.field.setForceChain(forceNodes.map((f) => ({
        kind: f.kind,
        weight: f.weight,
        enabled: f.enabled !== false,
        p: packForceParams(f.kind, f.params),
      })));
      this._chainSignature = signature;
      this._weights = new Float32Array(forceNodes.length);
    }

    if (this._weights.length !== forceNodes.length) {
      this._weights = new Float32Array(forceNodes.length);
    }
    for (let i = 0; i < forceNodes.length; i++) {
      const f = forceNodes[i];
      this._weights[i] = f.enabled === false ? 0 : f.weight;
      const p = packForceParams(f.kind, f.params);
      for (let k = 0; k < 8; k++) this.field.setSlotParam(i, k, p[k]);
    }
    this.field.setSlotWeights(this._weights);
  }

  applyEmitter(params) {
    this.field.setEmitter(params);
    // Point size is world-units -> pixels via the viewport height, so this
    // control changes the look without breaking resolution independence.
    if (Number.isFinite(params.pointScale)) {
      this.material.uniforms.uSizeScale.value = params.pointScale;
    }
  }

  setAudio(features) {
    this.field.setAudio(
      features.bands || null,
      features.rms || 0, features.bass || 0,
      features.mid || 0, features.high || 0,
      !!features.onset
    );
  }

  step(dt, fps) {
    this.field.step(dt, fps);
    this._sync();
  }

  _sync() {
    const g = this.geometry;
    const pos = g.getAttribute('position');
    const col = g.getAttribute('color');
    const siz = g.getAttribute('aSize');
    const alp = g.getAttribute('aAlpha');

    // These are views straight into WASM memory - copy before anything can
    // trigger a heap growth.
    const sp = this.field.positions();
    const sc = this.field.colors();
    const ss = this.field.sizes();
    const sa = this.field.alphas();

    const n3 = Math.min(pos.array.length, sp.length);
    const n1 = Math.min(siz.array.length, ss.length);

    pos.array.set(sp.subarray(0, n3));
    col.array.set(sc.subarray(0, n3));
    siz.array.set(ss.subarray(0, n1));
    alp.array.set(sa.subarray(0, n1));

    pos.needsUpdate = true;
    col.needsUpdate = true;
    siz.needsUpdate = true;
    alp.needsUpdate = true;
  }

  reset() {
    this.field.reset();
  }

  get aliveCount() { return this.field.getAliveCount(); }
  get frameIndex() { return this.field.getFrameIndex(); }

  dispose() {
    this.geometry.dispose();
    this.material.dispose();
    if (typeof this.field.delete === 'function') this.field.delete();
  }
}
